import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import isEmpty from 'lodash-es/isEmpty';

import { HourWidget, PriceWidget } from '@app/entities/flight-results-widget';
import { secondsToStringTime, SECONDS_IN_HOURS, stringTimeToSeconds, timeToNumber } from '@flight-results/mappers/filters.mapper';
import { FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { TimeFormatPipe } from '@app/pipes/time-format.pipe';
import { PickedRange } from '@shared/filters/entities/fetch-filters.response';
import { isNullOrUndefined } from '@app/util/util';
import { PriceAbbreviationPipe } from '@app/pipes/price-abbreviation.pipe';
import { RangeFilterPayload } from '@shared/filters/entities/range-filter';
import { PriceWidgetTypes } from '@flight-results/entities/widget';
import { environment as ENV } from '@environment';
import {
  ChipChoiceSize, ChipChoiceState, ChipChoiceType
} from '@design-system/components/molecules/chip-choice/entities/chip-choice.types';

@Component({
  selector: 'app-range-box-filter',
  templateUrl: './range-box-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RangeBoxFilterComponent implements OnInit, OnChanges {

  @Input() label: string;
  @Input() type: FilterTypes;
  @Input() sector: string;
  @Input() isOpenBox = false;
  @Input() isHotels: boolean;
  @Input() index = -1;
  @Input() individualFilter = false;
  @Input() widgets: PriceWidget[] | HourWidget[] = null;
  @Input() pickedRanges: PickedRange;
  @Input() isRadioBox = false;

  @Output() toggleIsOpenBox: EventEmitter<boolean> = new EventEmitter();
  @Output() values: EventEmitter<RangeFilterPayload> = new EventEmitter<RangeFilterPayload>();

  public showCustomRange = false;
  public priceForm: UntypedFormGroup;
  public priceItemForm = 'PRICE';
  public minTimeSelected: string = null;
  public maxTimeSelected: string = null;
  public selectedCustomValues: string[] | number[] = [];

  private touched: boolean;

  constructor(
    private readonly timeFormatPipe: TimeFormatPipe,
    private readonly priceAmountPipe: PriceAbbreviationPipe,
    private readonly translateService: TranslocoService
  ) { }

  ngOnInit(): void {
    if (!isEmpty(this.pickedRangesByType)) {
      const _widgets = Object.assign([], this.widgets);
      const activeWidget = _widgets.find(widget => this.isActive(widget));
      if (isNullOrUndefined(activeWidget)) {
        this.selectedCustomValues =
          this.isDepartureTimes || this.isSectorDurations ?
            this.pickedRangesByType.map(value => secondsToStringTime(+value, true)) :
            this.pickedRangesByType;
        this.customRange();
        this.touched = true;
      }
    }
    if (this.isPriceRange) {
      this.createPriceForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pickedRanges) {
      const currentValue = changes.pickedRanges?.currentValue;
      const previousValue = changes.pickedRanges?.previousValue;
      if (this.isDepartureTimes || this.isSectorDurations) {
        this.resetValues(currentValue?.[this.type][this.sector]);
      }

      if (this.showCustomRange && this.shouldShowCustomRange(currentValue, previousValue)) {
        this.customRange();
      }
    }
  }

  private get pickedRangesByType(): number[] {
    const ranges = this.isPriceRange ? this.pickedRanges[this.type] : this.pickedRanges[this.type][this.sector];
    return ranges ? ranges.map(value => +value) : null;
  }

  get chipChoiceStyle(): ChipChoiceType {
    return this.individualFilter ? ChipChoiceType.Secondary : ChipChoiceType.Primary;
  }

  get chipChoiceSize(): ChipChoiceSize {
    return this.individualFilter ? ChipChoiceSize.SM : ChipChoiceSize.RG;
  }

  get isPriceRange(): boolean {
    return this.type === FilterTypes.PriceRange;
  }

  get isDepartureTimes(): boolean {
    return this.type === FilterTypes.DepartureTimes;
  }

  get isSectorDurations(): boolean {
    return this.type === FilterTypes.SectorDurations;
  }

  get FilterTypes(): typeof FilterTypes {
    return FilterTypes;
  }

  get minPrice(): number {
    return (this.widgets as PriceWidget[])?.[0]?.PriceLimit?.[0];
  }

  get minTime(): number {
    return +(this.widgets as HourWidget[])?.[0]?.HourLimits?.[0];
  }

  get maxTime(): number {
    return +(this.widgets as HourWidget[])?.[this.widgets.length - 1]?.HourLimits?.[1];
  }

  get minFlightTime(): string {
    return this.minTime < 10 ? `0${this.minTime}:00` : `${this.minTime}:00`;
  }

  get maxFlightTime(): string {
    return this.maxTime < 10 ? `0${this.maxTime}:00` : this.maxTime === 24 ? '23:30' : `${this.maxTime}:00`;
  }

  get minSectorDuration(): string {
    const time = (this.widgets as HourWidget[])?.[0]?.HourLimits?.[0];
    return secondsToStringTime(+time);
  }

  get maxSectorDuration(): string {
    const time = (this.widgets as HourWidget[])?.[this.widgets.length - 1]?.HourLimits?.[1];
    return secondsToStringTime(+time);
  }

  get currencyCode(): string {
    return ENV.countryInfo.currency_code;
  }

  get isPrice(): boolean {
    return this.type === FilterTypes.PriceRange;
  }

  get individualClass(): boolean {
    return this.individualFilter && this.isPrice;
  }

  public chipChoiceState(state: boolean): ChipChoiceState {
    return state ? ChipChoiceState.Active : ChipChoiceState.Default;
  }

  public timeDuration(time: string, isMin: boolean): number {
    const days = time.split('.');
    const hasDays = days.length > 1;
    const _time = hasDays ? days[1].split(':') : time.split(':');
    const hour = hasDays ? +_time[0] + (+days[0] * 23) : +_time[0];
    const min = +_time[1];
    return !isMin && min > 0 ? hour + 1 : hour;
  }

  public isActive(widget: PriceWidget | HourWidget): boolean {
    let pickedRanges = this.pickedRanges[this.type];
    const range = this.isPriceRange ? (widget as PriceWidget).PriceLimit : (widget as HourWidget).HourLimits;
    if (this.isDepartureTimes) {
      pickedRanges = pickedRanges[this.sector]
        .map(r => this.timeFormatPipe.transform(secondsToStringTime(r)).replace('h', ''));
    } else if (this.isSectorDurations) {
      pickedRanges = pickedRanges[this.sector];
    }
    return Array.isArray(pickedRanges) && Array.isArray(range) && pickedRanges.toString() === range.toString();
  }

  public selectedValue(widget: PriceWidget | HourWidget): void {
    if (this.showCustomRange) {
      this.customRange();
    }
    const removable = this.isActive(widget);
    this.values.emit({
      range: this.isPriceRange ? (widget as PriceWidget).PriceLimit : (widget as HourWidget).HourLimits,
      removable
    });
  }

  public toggleOpenBox(): void {
    this.toggleIsOpenBox.emit(!this.isOpenBox);
  }

  public getInfoDepartureDescription(hourLimits: string[]): string {
    return `${secondsToStringTime((parseFloat(hourLimits[0]) * SECONDS_IN_HOURS), true)} - ` +
      `${secondsToStringTime((parseFloat(hourLimits[1]) * SECONDS_IN_HOURS), true)}`;
  }

  public getPriceRangeDescription(widget: PriceWidget): string {
    switch (widget.Type) {
      case PriceWidgetTypes.Middle:
        return `${this.priceAmountPipe.transform(widget.PriceLimit[0])}
        ${this.translateService.translate('GENERAL_WIDGET_MODULE.PRICE_WIDGET.FROM-TO')}
        ${this.priceAmountPipe.transform(widget.PriceLimit[1])}`;
      case PriceWidgetTypes.High:
        return `> ${this.priceAmountPipe.transform(widget.PriceLimit[0])}`;
      case PriceWidgetTypes.Cheap:
      default:
        return `< ${this.priceAmountPipe.transform(widget.PriceLimit[1])}`;
    }
  }

  public getSectorDurationDescription(hourLimits: string[]): string {
    return `${this.timeFormatPipe.transform(secondsToStringTime(+hourLimits[0]))} - ` +
      `${this.timeFormatPipe.transform(secondsToStringTime(+hourLimits[1]))}`;
  }

  public customRange(): void {
    this.showCustomRange = !this.showCustomRange;
  }

  public timeSelection(time: string, isInitial: boolean): void {
    this.touched = true;
    if (isInitial) {
      this.minTimeSelected = `${time}:00`;
    } else {
      this.maxTimeSelected = `${time}:00`;
    }
    const minTimeToEmit = this.minTimeSelected || `${this.minTime}:00:00`;
    const maxTimeToEmit = this.maxTimeSelected || `${this.maxTime}:00:00`;
    const _values = this.isDepartureTimes ?
      [timeToNumber(minTimeToEmit), timeToNumber(maxTimeToEmit)] :
      [stringTimeToSeconds(minTimeToEmit), stringTimeToSeconds(maxTimeToEmit)];
    this.values.emit({ range: _values, removable: false });
  }

  private createPriceForm(): void {
    const defaultPrice = +this.selectedCustomValues?.[1] || 0;
    this.priceForm = new UntypedFormGroup({
      [this.priceItemForm]: new UntypedFormControl(
        defaultPrice,
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(10),
          Validators.min(defaultPrice)
        ]
      )
    });
  }

  private resetValues(values: number[]): void {
    if (values.length === 0 && this.touched) {
      this.minTimeSelected = null;
      this.maxTimeSelected = null;
      this.selectedCustomValues = [];
      this.touched = false;
    }
  }

  private shouldShowCustomRange(currentValue: PickedRange, previousValue: PickedRange): boolean {
    const currentChanges = this.isPriceRange ? currentValue?.[this.type] : currentValue?.[this.type][this.sector];
    const previousChanges = this.isPriceRange ? previousValue?.[this.type] : previousValue?.[this.type][this.sector];
    return isEmpty(currentChanges) && !isEmpty(previousChanges);
  }

  public inputChange(event: number): void {
    this.values.emit({ range: [0, event], removable: false });
  }

}
